import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

// Models
import Call from "src/models/call";
import CallDDR from "src/models/call-ddr";
import PSTNCall from "src/models/pstn-call";
import { DTLayer } from "src/services/data-layer";
import { clearCallContext, setCallContext } from "src/services/logger";

// Types
import { type TransferObject } from "src/types";

type InitialState = {
  incomingCallId: null | string;
  twilioRoomName: null | string;
  twilioToken: null | string;
  udaQueueId: null | string;
  callDetails: null | Call | CallDDR;

  tempData: null | any;

  // Misc
  transferPaSelected: string;
};

const initialState: InitialState = {
  incomingCallId: null,
  twilioRoomName: null,
  twilioToken: null,
  udaQueueId: null,
  callDetails: null,

  tempData: null,

  // Misc
  transferPaSelected: "",
};

const contextSlice = createSlice({
  name: "call",
  initialState,
  reducers: {
    // => Status contollers
    initCall: (state, { payload }) => {
      state.incomingCallId = payload.callId;
      state.twilioRoomName = payload.twilioRoomName;
      state.twilioToken = payload.twilioToken;
      state.udaQueueId = payload.udaQueueId;

      setCallContext({ callId: payload.callId });

      DTLayer.dispatch("initCall", {
        dataLayer: {
          event: "",
          callData: {
            id: payload.callId,
          },
        },
      });
    },

    callDataLoaded: (
      state,
      {
        payload,
      }: {
        payload: {
          call: any;
          incomingCallId?: string;
          lastWsEvent?: { currentExternalCallNumber: string };
          udaQueueId?: string;
          twilioRoomName?: string;
          twilioToken?: string;
        };
      },
    ) => {
      state.callDetails = ["ICOM_VOZ", "PSTN"].includes(payload.call.callType) ? new CallDDR(payload.call) : new Call(payload.call);

      state.incomingCallId = payload.incomingCallId || state.incomingCallId;

      // If is transference
      if (payload.twilioRoomName && payload.twilioToken) {
        state.twilioRoomName = payload.twilioRoomName;
        state.twilioToken = payload.twilioToken;
      }

      // Save temp data
      if (payload.lastWsEvent) state.tempData = payload.lastWsEvent;

      // Uda QueueId
      if (payload.udaQueueId) state.udaQueueId = payload.udaQueueId;

      setCallContext({
        callId: state.callDetails.id,
        aderenteSlug: state.callDetails.aderenteSlug,
        callType: state.callDetails.callType,
      });

      DTLayer.dispatch("callDataLoaded", {
        callData: {
          id: state.callDetails.id,
          aderenteSlug: state.callDetails.aderenteSlug,
        },
      });
    },

    updateCall: (state, { payload: rawCallData }) => {
      const newCallData = new Call(rawCallData);
      state.callDetails = _.merge(state.callDetails, newCallData);
    },

    updateCallObject: (state, { payload }) => {
      const merged = _.merge(state.callDetails, payload);
      state.callDetails = merged;
    },
    updateTwilioTokens: (state, { payload }) => {
      if (payload.twilioRoomName) state.twilioRoomName = payload.twilioRoomName;

      if (payload.twilioToken) state.twilioToken = payload.twilioToken;
    },

    callFeaturesLoaded: (state, { payload }) => {
      state.callDetails!.features = [...payload.features];
    },

    setTransferStatus: (state, { payload }: { payload: TransferObject }) => {
      state.callDetails!.transferenceStatus = payload;
    },

    callEnded: (state) => {
      // Regen
      state.incomingCallId = null;
      state.twilioRoomName = null;
      state.twilioToken = null;
      state.udaQueueId = null;
      state.callDetails = null;

      clearCallContext();

      DTLayer.dispatch("callEnded", {
        callData: {
          id: null,
          aderenteSlug: null,
        },
      });
    },

    // Toggle Transfer PA
    togglePaSelected: (state, { payload }) => {
      state.transferPaSelected = payload;
    },

    // Video Controls
    setTrackState: (state, { payload }) => {
      const { isVideoEnabled, isMicEnabled, isAudioEnabled } = payload;

      if (state.callDetails) {
        state.callDetails.isMicEnabled = isMicEnabled !== undefined ? isMicEnabled : state.callDetails?.isMicEnabled;

        state.callDetails.isVideoEnabled = isVideoEnabled !== undefined ? isVideoEnabled : state.callDetails?.isVideoEnabled;

        state.callDetails.isAudioEnabled = isAudioEnabled !== undefined ? isAudioEnabled : state.callDetails?.isAudioEnabled;
      }
    },

    // Text Message
    addMessage: (state, { payload }) => {
      state.callDetails?.messageList.push(payload);
    },

    // Dialer things
    addDialerInstance: (state, { payload }) => {
      state.callDetails?.dialerInstances?.push(payload);
    },

    addPstnCall: (state, { payload }: { payload: PSTNCall }) => {
      state.callDetails?.pstnNumbers.push(payload);
    },

    updatePstnCall: (state, { payload }: { payload: PSTNCall }) => {
      if (state.callDetails?.pstnNumbers)
        state.callDetails.pstnNumbers = state.callDetails?.pstnNumbers.map((p) => {
          if (p.internalId === payload.internalId) p = payload;
          return p;
        });
    },

    toggleSupervisor: (state, { payload }) => {
      if (state.callDetails) state.callDetails.waitingSupervisor = payload;
    },

    setPstnAsActive: (state, { payload }: { payload: PSTNCall }) => {
      if (state.callDetails?.pstnNumbers)
        state.callDetails.pstnNumbers = state.callDetails?.pstnNumbers.map((pstn) => {
          if (pstn.internalId === payload.internalId) pstn.active = true;
          else pstn.active = false;

          return pstn;
        });
    },
  },
});

export const {
  // Call Events
  initCall,
  callDataLoaded,
  callEnded,
  updateCall,
  updateCallObject,
  updateTwilioTokens,

  // Set status
  setTrackState,
  setTransferStatus,
  callFeaturesLoaded,
  togglePaSelected,
  toggleSupervisor,

  // Messages
  addMessage,

  // Dialer
  addDialerInstance,
  addPstnCall,
  updatePstnCall,
  setPstnAsActive,
} = contextSlice.actions;
export default contextSlice.reducer;
